import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { Textarea } from '@repo/shared-client/components/ui'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { EventInfo } from 'src/lib/fragments'
import type { CancelEventMutation, CancelEventMutationVariables } from 'types/graphql'

export const CANCEL_EVENT: TypedDocumentNode<CancelEventMutation, CancelEventMutationVariables> = gql`
  mutation CancelEvent($eventIdOrSlug: String!, $input: CancelEventInput!) {
    cancelEvent(eventIdOrSlug: $eventIdOrSlug, input: $input) {
      ...EventInfo
    }
  }
  ${EventInfo}
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const EventCancel = ({
  event,
  ...props
}: CustomModalProps & {
  event: {
    id: string
    name: string
    slug?: string
  }
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [cancellationReason, setCancellationReason] = useState('')

  const [cancelEvent] = useMutation(CANCEL_EVENT, {
    onCompleted: () => {
      closeDialog()
      navigate({ to: '/event/$eventIdOrSlug', params: { eventIdOrSlug: event.slug || event.id } } as never)
      toast.success(t('Event cancelled'))
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    cancelEvent({
      variables: {
        eventIdOrSlug: event.slug || event.id,
        input: {
          cancellationReason: cancellationReason.trim(),
        },
      },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      beforeControls={
        <div className="space-y-2">
          <p className="font-semibold text-sm">{t('Reason for cancellation for your attendees')}</p>
          <Textarea
            autoComplete="off"
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder={t('Enter cancellation reason')}
            value={cancellationReason}
          />
        </div>
      }
      confirmDisabled={cancellationReason.trim().length === 0}
      confirmText={t('Cancel Event')}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          setShowSpinnerValue(false)
          setCancellationReason('')
        }
      }}
      open={props.open}
      text={t('Cancelling this event will notify all going attendees. This action cannot be undone.')}
      title={t('Cancel Event')}
      variant="basic"
    />
  )
}

export default EventCancel
